import api from './api';
import { cachedGet } from '../utils/lookupCache';

/**
 * Generic Data Management table-maintenance client.
 *
 * Talks to the whitelist-driven /tablemaint engine (the generalized successor to
 * /quote-tablemaint): one set of CRUD routes keyed by table. The backend resolves
 * the schema (cms/quote/safety, env-driven) and owns the authoritative column
 * whitelist, dup-guard, validation, and audit logging.
 *
 * Soft-delete is the CHAR(1) 'Y'/'N' convention; rows come back with the raw
 * value and <TablemaintPage> normalizes via the table's `softDelete` column.
 */

export type TablemaintRow = Record<string, any>;

/** One column the grid's field picker may offer, from GET /tablemaint/{table}/columns.
 *  `type` is inferred from the DB column (date/datetime/number/textarea/text); the
 *  registry field spec's own type wins wherever one exists. */
export interface AddableColumn {
  name: string;
  type: 'text' | 'textarea' | 'number' | 'date' | 'datetime';
  max_length: number | null;
  /** Read-only SQL expression rather than a stored column. */
  computed: boolean;
  /** Writable through the engine (create/update). */
  editable: boolean;
  /** Whether the field picker may offer this column. False on a table whose grid comes
   *  from a hand-written SELECT, where an added column would arrive empty — such a
   *  table still publishes its columns so their `mass_edit` role is known. Absent on
   *  an older backend, which only ever published addable ones. */
  addable?: boolean;
  /** How this column may be written across many rows at once, or null when it may
   *  not be. The backend owns this — the grid can only narrow it further (it can see
   *  the registry's FK pickers, which the backend cannot). Kept separate from `type`,
   *  which drives DISPLAY: a char(1) shows as its raw letter but bulk-edits as Y/N. */
  mass_edit: 'yn' | 'number' | 'date' | 'select' | 'text' | null;
}

/** One record's staged edits — any number of columns on that record. */
export interface MassEditChange {
  recno: number | string;
  values: Record<string, unknown>;
}

/** Outcome of a mass edit. Per-row, not all-or-nothing: a record that fails its
 *  validator leaves the others written, and comes back named so the grid can keep
 *  it staged for a retry. */
export interface MassEditResult {
  /** Every column the batch touched. */
  columns: string[];
  updated: number[];
  /** Already held the target value — nothing written, and not a failure. */
  unchanged: number[];
  failed: { recno: number | string; error: string }[];
}

/** One order in a per diem recalculate result: which fields would change
 *  (or did change), old -> new. */
export interface PerdiemRecalcOrder {
  pronumber: number;
  container: string;
  scac: string;
  movetype: string;
  size: string;
  changes: Record<string, { old: string; new: string }>;
}

/** An order whose per diem already matches the rules exactly ("Match" tab) —
 *  current values included for display. */
export interface PerdiemRecalcMatch {
  pronumber: number;
  container: string;
  scac: string;
  movetype: string;
  current: Record<string, string>;
}

export interface PerdiemRecalcResult {
  scope: { billto: number; customercode: number; brokercode: number };
  /** false = confirm is disabled server-side (V1 still owns per diem updates) */
  apply_enabled: boolean;
  /** true when this response reflects writes that actually happened */
  applied: boolean;
  counts: { candidates: number; updates: number; unchanged: number; skipped: number };
  updates: PerdiemRecalcOrder[];
  unchanged: PerdiemRecalcMatch[];
  skipped: { reason: string; count: number; pros: number[] }[];
}

const base = (table: string) => `/tablemaint/${encodeURIComponent(table)}`;

const tablemaintService = {
  /** GET /tablemaint/{table} — active rows by default; pass includeDeleted for all,
   *  includeArchived for tables that fold in an archive table (e.g. EDI Events), and
   *  q + pageSize for server-searched, capped tables (huge tables that can't load
   *  every row into the browser). */
  async list(
    table: string,
    opts: {
      includeDeleted?: boolean;
      includeArchived?: boolean;
      /** Relax a table's default non-blank filter (cfg.list_nonblank) — the
       *  narrowing twin of includeDeleted/includeArchived. */
      includeBlank?: boolean;
      q?: string;
      pageSize?: number;
      /** Column-scoped filters (?col=val) forwarded to the backend list_sql, e.g. a
       *  deep-link pre-filtering a server-searched grid to a specific record. */
      filters?: Record<string, string>;
      /** Extra whitelisted columns to select alongside the standard list set —
       *  the grid's field picker. Unknown names are dropped server-side. */
      fields?: string[];
    } = {},
    signal?: AbortSignal,
  ): Promise<TablemaintRow[]> {
    const params: Record<string, string> = {};
    if (opts.includeDeleted) params.include_deleted = 'true';
    if (opts.includeArchived) params.include_archived = 'true';
    if (opts.includeBlank) params.include_blank = 'true';
    if (opts.q) params.q = opts.q;
    if (opts.pageSize && opts.pageSize > 0) params.page_size = String(opts.pageSize);
    if (opts.fields && opts.fields.length) params.fields = opts.fields.join(',');
    if (opts.filters) {
      for (const [k, v] of Object.entries(opts.filters)) {
        if (v !== '' && v != null) params[k] = v;
      }
    }
    const res = await api.get(base(table), { params, signal });
    return res.data as TablemaintRow[];
  },

  /** POST /tablemaint/{table}/list — rows matching a pasted list of values.
   *
   *  The server-resolved half of TanTable's "Match List" filter, for the CAPPED tables
   *  (EDI Invoices, EDI 997s). Those grids hold their most recent page rather than the
   *  whole table, so matching a list against the loaded rows reports "not found" for
   *  anything older than the cap — indistinguishable, to the user, from a value we
   *  genuinely never sent. This asks the table itself and comes back uncapped, so the
   *  grid's "43 of 50 matched" is finally true.
   *
   *  POST rather than a query string for the same reason customSearch is: a few
   *  thousand values would blow past URL and WAF length limits. The backend validates
   *  `inColumn` against its own whitelist and 400s on anything else. */
  async listByValues(
    table: string,
    inColumn: string,
    values: string[],
    opts: {
      includeDeleted?: boolean;
      includeArchived?: boolean;
      fields?: string[];
    } = {},
    signal?: AbortSignal,
  ): Promise<TablemaintRow[]> {
    const res = await api.post(
      `${base(table)}/list`,
      {
        in_column: inColumn,
        in_values: values,
        include_deleted: !!opts.includeDeleted,
        include_archived: !!opts.includeArchived,
        ...(opts.fields && opts.fields.length ? { fields: opts.fields.join(',') } : {}),
      },
      { signal },
    );
    return res.data as TablemaintRow[];
  },

  /** GET /tablemaint/{table}/columns — the column universe the field picker may
   *  offer. Static per table (it is the backend whitelist), so it rides the shared
   *  lookup cache rather than refetching on every visit. Empty for list_sql tables,
   *  whose hand-written SELECT `fields` cannot extend. */
  async columns(table: string, signal?: AbortSignal): Promise<AddableColumn[]> {
    const res = await cachedGet<AddableColumn[]>(`${base(table)}/columns`, { signal });
    return res.data ?? [];
  },

  /** PUT /tablemaint/{table}/bulk — apply staged grid edits.
   *
   *  ONE call however many columns, records and distinct values are staged: the
   *  payload is per-record, so a drag-select setting 40 rows to 'Y' and a handful of
   *  individually typed cells cost the same single round trip. The backend runs each
   *  record through the same validation and history logging as a drawer save and
   *  names the ones that failed. */
  async bulkUpdate(table: string, changes: MassEditChange[]): Promise<MassEditResult> {
    const res = await api.put(`${base(table)}/bulk`, { changes });
    return res.data as MassEditResult;
  },

  /** GET /tablemaint/{table}/{recno} — single row. */
  async get(table: string, recno: number | string): Promise<TablemaintRow> {
    const res = await api.get(`${base(table)}/${recno}`);
    return res.data as TablemaintRow;
  },

  /** POST /tablemaint/{table} — create; returns the new key. */
  async create(table: string, values: TablemaintRow): Promise<{ recno: number }> {
    const res = await api.post(base(table), values);
    return res.data as { recno: number };
  },

  /** PUT /tablemaint/{table}/{recno} — update changed fields. */
  async update(
    table: string,
    recno: number | string,
    values: TablemaintRow,
  ): Promise<{ recno: number }> {
    const res = await api.put(`${base(table)}/${recno}`, values);
    return res.data as { recno: number };
  },

  /** DELETE /tablemaint/{table}/{recno} — soft-delete. */
  async remove(table: string, recno: number | string): Promise<{ recno: number }> {
    const res = await api.delete(`${base(table)}/${recno}`);
    return res.data as { recno: number };
  },

  /** POST /tablemaint/customer-perdiem/recalculate — per diem "Update Open
   *  Orders" flow. apply=false previews (nothing written); apply=true is the
   *  explicit confirm and writes the previewed updates. */
  async perdiemRecalculate(scope: {
    billto: number;
    customercode: number;
    brokercode: number;
    apply: boolean;
  }): Promise<PerdiemRecalcResult> {
    const res = await api.post('/tablemaint/customer-perdiem/recalculate', scope);
    return res.data as PerdiemRecalcResult;
  },
};

export default tablemaintService;
